import {
  GetOrganisationQuery,
  GetOrganisationQueryVariables,
  OrganisationEntityDto,
} from '@/lib/types/graphql';
import { useRouter } from 'next/router';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { GET_ORGANISATION } from '@/lib/queries/organisation.query';
import { useQuery } from 'urql';

type OrganisationContextDto = Pick<OrganisationEntityDto, 'id' | 'name'>;

export interface OrganisationContextProps {
  organisation?: OrganisationContextDto;
  updateOrganisation?: (organisation: OrganisationContextDto, replace?: boolean) => void;
}

export interface OrganisationDrawerState {
  open: boolean;
}

export interface OrganisationManagerContextProps extends OrganisationContextProps {
  drawer: OrganisationDrawerState;
  openDrawer: () => void;
  closeDrawer: () => void;
}

/**
 * Context for organisation management including organisation state and drawer state.
 */
export const OrganisationManagerContext = createContext<OrganisationManagerContextProps>({
  organisation: undefined,
  updateOrganisation: undefined,
  drawer: { open: false },
  openDrawer: () => {},
  closeDrawer: () => {},
});

/**
 * Hook to access the organisation manager context.
 * Use this throughout the app to access organisation state and drawer controls.
 */
export const useOrganisationManagerContext = () => useContext(OrganisationManagerContext);

export const useOrganisationContext = () => {
  const { organisation, updateOrganisation } = useOrganisationManagerContext();
  return { organisation, updateOrganisation };
};

export const useOrganisationDrawerContext = () => {
  const { drawer, openDrawer, closeDrawer } = useOrganisationManagerContext();
  return {
    state: drawer,
    handleOpen: openDrawer,
    handleClose: closeDrawer,
  };
};

/**
 * Custom hook to manage organisation state and synchronize it with URL parameters.
 * This centralizes organisation management logic including drawer state.
 */
export const useOrganisationManager = (): OrganisationManagerContextProps => {
  const [organisation, setOrganisation] = useState<OrganisationContextDto | undefined>(undefined);
  const [drawer, setDrawer] = useState<OrganisationDrawerState>({ open: false });
  const organisationRef = useRef<OrganisationContextDto | undefined>(organisation);
  const router = useRouter();

  const urlOrganisationId = router.query.organisationId
    ? parseInt(router.query.organisationId as string, 10)
    : undefined;

  // Only fetch when we have an organisationId but no name (when assigning URL on page refresh or direct link)
  const shouldFetch = !!(urlOrganisationId && !organisation?.name);
  const [{ data }] = useQuery<GetOrganisationQuery, GetOrganisationQueryVariables>({
    query: GET_ORGANISATION,
    variables: { id: urlOrganisationId! },
    pause: !shouldFetch,
  });

  // Keep ref synchronized with state
  useEffect(() => {
    organisationRef.current = organisation;
  }, [organisation]);

  const updateOrganisation = useCallback(
    (updatedOrganisation: OrganisationContextDto | undefined, replace = false) => {
      // Avoid unnecessary navigation and state updates when the organisation ID hasn't changed
      if (organisationRef.current?.id === updatedOrganisation?.id) {
        return;
      }

      // Update state first, then navigate
      // The ref will be synchronized via the useEffect
      setOrganisation(updatedOrganisation);

      const query = { ...router.query };
      if (updatedOrganisation) {
        query.organisationId = `${updatedOrganisation.id}`;
      }

      if (replace) {
        router.replace({ query });
      } else {
        router.push({ query });
      }
    },
    // Note: organisationRef is intentionally omitted from dependencies as it's a ref and doesn't cause re-renders.
    // We use the ref specifically to avoid recreating this callback on every state change.
    // router is included to ensure we always use the latest router instance for navigation.
    [router],
  );

  const openDrawer = useCallback(() => {
    setDrawer({ open: true });
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawer({ open: false });
  }, []);

  // Synchronizes the organisation context with the organisationId query parameter in the URL.
  useEffect(() => {
    if (data?.organisation && updateOrganisation) {
      // Use replace=true to avoid adding extra history entries during initial URL-based sync
      updateOrganisation(
        {
          id: data.organisation.id,
          name: data.organisation.name,
        },
        true,
      );
    }
  }, [data?.organisation, updateOrganisation]);

  return {
    organisation,
    updateOrganisation,
    drawer,
    openDrawer,
    closeDrawer,
  };
};
